/**
 * @fileoverview Microsoft account connection UI (MOR-555 Work OS Sync).
 *
 * User-level provider connections (OAuth via WorkOS Pipes) shared by:
 * - the per-job Integrations panel (-ui.job-integrations-panel.tsx)
 * - the firm-wide Settings → Integrations page
 *
 * Connect opens the WorkOS authorize URL in a popup; status is polled until
 * it flips. Disconnect runs through our own Convex action (the Pipes widget
 * 403s under AuthKit session tokens).
 */

import { type ReactNode, useEffect, useRef, useState } from "react";
import { useAction } from "convex/react";
import { api } from "@cvx/_generated/api";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { toast } from "sonner";
import { AlertTriangle, Loader2, UserRound } from "lucide-react";
import {
  type AccountInfo,
  type PipesProvider,
  ACCOUNT_PROVIDERS,
  parseAccount,
  PROVIDER_LABELS,
  PROVIDER_LOGOS,
} from "./-microsoft-providers";

export function ProviderLogo({
  provider,
  className = "h-5 w-5",
}: {
  provider: PipesProvider;
  className?: string;
}) {
  return (
    <img
      src={PROVIDER_LOGOS[provider]}
      alt={PROVIDER_LABELS[provider]}
      className={`shrink-0 ${className}`}
    />
  );
}

// ---------------------------------------------------------------------------
// Section (bordered card with heading, as shown on the job Integrations tab)
// ---------------------------------------------------------------------------

export function MicrosoftAccountSection({
  statuses,
  statusesLoading,
  onChanged,
  renderConnectedAction,
  renderConnectedDetails,
}: {
  statuses: Record<string, AccountInfo> | undefined;
  statusesLoading: boolean;
  onChanged: () => void;
  /**
   * Optional extra action rendered next to Disconnect when a provider is
   * connected (e.g. the job-level "Set up sync" / "Sync now" entry point).
   * Only supplied in a job context — the firm Settings page leaves it undefined.
   */
  renderConnectedAction?: (provider: PipesProvider) => ReactNode;
  /**
   * Optional details rendered below the row when a provider is connected
   * (e.g. the job-level sync folder + last-run stats). Should return null when
   * there is nothing to show. Only supplied in a job context.
   */
  renderConnectedDetails?: (provider: PipesProvider) => ReactNode;
}) {
  return (
    <div className="rounded-lg border p-4">
      <div className="flex items-center gap-2">
        <UserRound className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium">Connected accounts</span>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        You are only required to connect your account once. When your account is
        connected, you are able to sync folders on each job.
      </p>
      <div className="mt-3 space-y-2">
        <MicrosoftAccountRows
          statuses={statuses}
          statusesLoading={statusesLoading}
          onChanged={onChanged}
          renderConnectedAction={renderConnectedAction}
          renderConnectedDetails={renderConnectedDetails}
        />
      </div>
    </div>
  );
}

/** Just the provider rows, for embedding in differently-styled cards. */
export function MicrosoftAccountRows({
  statuses,
  statusesLoading,
  onChanged,
  renderConnectedAction,
  renderConnectedDetails,
}: {
  statuses: Record<string, AccountInfo> | undefined;
  statusesLoading: boolean;
  onChanged: () => void;
  renderConnectedAction?: (provider: PipesProvider) => ReactNode;
  renderConnectedDetails?: (provider: PipesProvider) => ReactNode;
}) {
  return (
    <>
      {ACCOUNT_PROVIDERS.map((provider) => (
        <AccountStatusRow
          key={provider}
          provider={provider}
          info={
            statusesLoading
              ? undefined
              : (statuses?.[provider] ?? { status: "not_connected" })
          }
          onChanged={onChanged}
          renderConnectedAction={renderConnectedAction}
          renderConnectedDetails={renderConnectedDetails}
        />
      ))}
    </>
  );
}

function AccountStatusRow({
  provider,
  info,
  onChanged,
  renderConnectedAction,
  renderConnectedDetails,
}: {
  provider: PipesProvider;
  info: AccountInfo | undefined;
  onChanged: () => void;
  renderConnectedAction?: (provider: PipesProvider) => ReactNode;
  renderConnectedDetails?: (provider: PipesProvider) => ReactNode;
}) {
  const status = info?.status;
  const [busy, setBusy] = useState(false);
  const pollRef = useRef<number | null>(null);

  const getConnectUrl = useAction(
    api.integrations.workOsSync.browseActions.getConnectUrl,
  );
  const getStatuses = useAction(
    api.integrations.workOsSync.browseActions.getProviderAccountStatuses,
  );
  const disconnectAccount = useAction(
    api.integrations.workOsSync.browseActions.disconnectProviderAccount,
  );

  // Clear any poll on unmount.
  useEffect(() => {
    return () => {
      if (pollRef.current !== null) window.clearInterval(pollRef.current);
    };
  }, []);

  async function handleConnect() {
    setBusy(true);
    try {
      const url = await getConnectUrl({ provider });
      const popup = window.open(
        url,
        "morgan-pipes-connect",
        "width=600,height=720",
      );
      if (!popup) {
        toast.error("Popup blocked — allow popups for this site and retry.");
        setBusy(false);
        return;
      }
      // Poll until the connection appears or the popup is closed.
      pollRef.current = window.setInterval(() => {
        void (async () => {
          if (popup.closed) {
            if (pollRef.current !== null) window.clearInterval(pollRef.current);
            pollRef.current = null;
            setBusy(false);
            onChanged();
            return;
          }
          const statuses = await getStatuses({});
          if (statuses[provider]?.status === "connected") {
            if (pollRef.current !== null) window.clearInterval(pollRef.current);
            pollRef.current = null;
            popup.close();
            setBusy(false);
            toast.success(`${PROVIDER_LABELS[provider]} connected`);
            onChanged();
          }
        })();
      }, 2500);
    } catch (error) {
      setBusy(false);
      toast.error(
        error instanceof Error ? error.message : "Failed to start connection",
      );
    }
  }

  /**
   * Xero-only: re-run the OAuth consent to authorise an ADDITIONAL
   * organisation on the already-connected account. The status is already
   * "connected" so it can't flip — just wait for the popup to close, then
   * refresh so the new organisation shows up.
   */
  async function handleAddOrganisation() {
    setBusy(true);
    try {
      const url = await getConnectUrl({ provider });
      const popup = window.open(
        url,
        "morgan-pipes-connect",
        "width=600,height=720",
      );
      if (!popup) {
        toast.error("Popup blocked — allow popups for this site and retry.");
        setBusy(false);
        return;
      }
      pollRef.current = window.setInterval(() => {
        if (popup.closed) {
          if (pollRef.current !== null) window.clearInterval(pollRef.current);
          pollRef.current = null;
          setBusy(false);
          onChanged();
        }
      }, 1500);
    } catch (error) {
      setBusy(false);
      toast.error(
        error instanceof Error ? error.message : "Failed to start connection",
      );
    }
  }

  async function handleDisconnect() {
    setBusy(true);
    try {
      const result = await disconnectAccount({ provider });
      toast.success(
        result === "disconnected"
          ? `${PROVIDER_LABELS[provider]} account disconnected`
          : "No connected account found.",
      );
      onChanged();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to disconnect",
      );
    } finally {
      setBusy(false);
    }
  }

  const account = info?.account ? parseAccount(info.account) : null;

  return (
    <div className="rounded-md border px-3 py-2.5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <ProviderLogo provider={provider} className="h-6 w-6" />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">
                {PROVIDER_LABELS[provider]}
              </span>
              {status === undefined ? (
                <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
              ) : status === "connected" ? (
                <Badge color="green" variant="soft" size="sm">
                  Connected
                </Badge>
              ) : status === "needs_reauthorization" ? (
                <Badge color="amber" variant="soft" size="sm">
                  <AlertTriangle className="h-3 w-3" /> Reauthorization required
                </Badge>
              ) : status === "provider_not_configured" ? (
                <Badge color="amber" variant="soft" size="sm">
                  <AlertTriangle className="h-3 w-3" /> Not enabled in WorkOS
                </Badge>
              ) : (
                <Badge color="gray" variant="outline" size="sm">
                  Not connected
                </Badge>
              )}
            </div>
            {status === "connected" && account && (
              <div className="mt-0.5 flex items-center gap-1.5">
                <span className="truncate text-xs text-muted-foreground">
                  {account.email}
                </span>
                {account.kind && (
                  <Badge color="gray" variant="soft" size="sm">
                    {account.kind}
                  </Badge>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {status === "connected" ? (
            <>
              {renderConnectedAction?.(provider)}
              {provider === "xero" && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() => void handleAddOrganisation()}
                >
                  {busy && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                  Add organisation
                </Button>
              )}
              <Button
                size="sm"
                variant="destructive"
                disabled={busy}
                onClick={() => void handleDisconnect()}
              >
                {busy && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                Disconnect
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              disabled={
                busy ||
                status === undefined ||
                status === "provider_not_configured"
              }
              onClick={() => void handleConnect()}
            >
              {busy && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
              {status === "needs_reauthorization" ? "Reauthorize" : "Connect"}
            </Button>
          )}
        </div>
      </div>
      {status === "connected" && renderConnectedDetails?.(provider)}
    </div>
  );
}
